import 'vuetify/styles'                         // ① Vuetify core CSS
import '@mdi/font/css/materialdesignicons.css'  // ② Icons
import '@blueprint/frontend-basics/dist/blueprint-basics.es.css'
import 'reflect-metadata' // This is required for decorators to work properly

import BlueprintBasics, {
  createVuetifyInstance,
  createAppRouter,
  createLanguageStore,
  createAppI18n,
  defaultLang,
  useUserStore,
  Routes,
  type LanguageStore,
  type SupportedLang
} from '@blueprint/frontend-basics'
import { createPinia } from 'pinia'
import { createApp } from 'vue'

import App from './App.vue'
import routes from './router/index.ts'
import { additionalMessages } from './i18n/i18n.ts'
import { Capacitor } from '@capacitor/core'
import { App as CapacitorApp, type URLOpenListenerEvent } from '@capacitor/app'
import { useServiceStore } from './stores/service.store.ts'
// import * as config from './helpers/config.helper.ts'

// // Debug function to print all config values on startup
// function printConfigDebugTable() {
//   const configValues = [
//     { name: 'paddleMode', value: config.paddleMode },
//     { name: 'paddleClientToken', value: config.paddleClientToken ? '***SET***' : null },
//     { name: 'paddleEarlyAccessPriceId', value: config.paddleEarlyAccessPriceId },
//     { name: 'paddleSuccessUrl', value: config.paddleSuccessUrl },
//     { name: 'posthogToken', value: config.posthogToken ? '***SET***' : null },
//     { name: 'isTrackingDisabled', value: config.isTrackingDisabled },
//     { name: 'appId', value: config.appId },
//     { name: 'keycloakURL', value: config.keycloakURL },
//     { name: 'keycloakRealm', value: config.keycloakRealm },
//     { name: 'keycloakClientId', value: config.keycloakClientId },
//     { name: 'domainURL', value: config.domainURL },
//     { name: 'backendURL', value: config.backendURL },
//     { name: 'isProduction', value: config.isProduction },
//   ]

//   console.group('🔧 Frontend Config Values')
//   console.table(configValues)
//   console.groupEnd()
// }

// // Print config debug table on startup
// printConfigDebugTable()

// Create app and pinia
const app = createApp(App)
const pinia = createPinia()
app.use(pinia)
app.use(BlueprintBasics)

// Define supported languages
const supportedLanguages: SupportedLang[] = ['en', 'fr', 'de', 'es', 'ar']

// Create language store with supported languages
const useLanguageStore = createLanguageStore(supportedLanguages)
const languageStore: LanguageStore = useLanguageStore()

// Provide the languageStore for components to use
app.provide('languageStore', languageStore)

// Create i18n instance with supported languages
const i18n = createAppI18n({
  supportedLanguages,
  defaultLanguage: defaultLang,
  additionalMessages,
})

// Create vuetify instance with our custom i18n
const vuetify = createVuetifyInstance(i18n)

// Initialize router with additional routes and i18n options
// Use type assertion to resolve module boundary issues
const router = createAppRouter(routes as any, {
  i18n,
  defaultLang,
  languageStore
})

// Use the Vue plugins with type assertions to handle module boundary issues
app.use(router)
app.use(vuetify)
app.use(i18n)

if (Capacitor.isNativePlatform()) {
  CapacitorApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {

    console.log('Received appUrlOpen event', event.url)
    // The event URL is the redirect from Keycloak after a successful login.
    // It contains authentication details as query parameters.
    // We need to get these parameters and reload the app at the current view, with these new parameters.
    // This allows keycloak-js to process the login result upon re-initialization.
    try {
      console.log('event.url', event.url)
      const url = event.url
      console.log('url', url)

      // Extract the hash fragment from the deep link URL.
      // This contains the OIDC parameters from Keycloak.
      const hash = url.substring(url.indexOf('#'))
      
      // Use the router to navigate to the home route and append the hash.
      // We then immediately reload the page so that keycloak-js can initialize
      // again and process the parameters from the URL hash.
      router.push({ name: Routes.HOME, hash: hash }).then(() => {
        window.location.reload()
      })
      
    } catch (err) {
      console.error('Failed to handle appUrlOpen event', err)
    }
  })
}

const userStore = useUserStore()
const serviceStore = useServiceStore()
app.provide('serviceStore', serviceStore)
userStore.init(serviceStore, router)

app.mount('#app')
